import { getInstanceDefinition } from '../cards/cardLookup';
import { getHandler } from '../cards/effectRegistry/index';
import { getPlayerById } from '../state/selectors';
import {
  discardFromHand, placeInFrontOfPlayer, destroyFromHand, pickRandomInstanceId,
} from '../cards/effectHelpers';
import { isWeaponEquipped } from '../combat/attackMath';
import { PHASES } from '../constants';
import { endTurn } from './turnActions';
import { performBuy } from './villageActions';

function usageKey(instanceId, timing, abilityIndex) {
  return `${instanceId}:${timing}:${abilityIndex}`;
}

// Numenera, Clever: "This hero can only use one ability." The only hero
// group with this restriction, so a small explicit set - see
// useHandCardAbility below.
const SINGLE_ABILITY_PER_CARD_GROUPS = new Set(['clever']);

// Base rulebook (TS7_Rules_Web.pdf, p.16-17): "Weapons are a special type of
// card. A weapon adds nothing to the party and you cannot use its Dungeon
// abilities unless it is equipped to a hero." A general rule, independent of
// whether a given weapon's own printed text happens to mention "equipped" -
// most don't (Blessed Hammer, Cursed Dagger, Jondul Bow, Shock Nodule,
// Taproot Blade, Thorn Caltrops), so without this gate useHandCardAbility
// (the single choke point every village/dungeon/spoils ability passes
// through) would let them fire straight out of hand, unequipped. Frost Giant
// Axe is the sole printed exception in the pool ("You can use this ability
// while this card is not equipped.") - a small explicit allowlist by
// definition id, matching this file's own SINGLE_ABILITY_PER_CARD_GROUPS
// convention just above.
const WEAPON_DUNGEON_ABILITY_EQUIP_EXEMPT_IDS = new Set(['village-frost-giant-axe']);

// Caldera Dragon's Global ability ("After each time a player uses an ability
// that drew 1 or more cards, that player must discard 1 card") - the only
// ToR card using the Global timing keyword that isn't a one-off special case
// wired directly into the engine (see bearer-stramst/bearer-belac in
// effectRegistryCoverage.test.js's KNOWN_GAPS). Global abilities have no
// dedicated dispatch path of their own (nothing in the engine ever fires
// timing 'global' generically), so this hooks the one concrete trigger the
// card's text actually needs - directly in useHandCardAbility, the single
// choke point every village/dungeon/spoils ability already passes through.
function isCalderaDragonPresent(state) {
  return state.dungeonHall.some((id) => id && getInstanceDefinition(state, id).id === 'monster-caldera-dragon');
}

// Matt asked for this directly: "it didn't let me choose which card I
// wanted to discard and it should let me choose." Can't resolve this choice
// up front like every other card-effect choice does (the "choices supplied
// before dispatch" convention) - the drawn card's identity isn't knowable
// until the triggering ability's draw has actually happened, so there's no
// candidate list to show a picker for beforehand. Instead this leaves a
// meta.pendingCalderaDragonDiscard marker and does NOT discard yet;
// dispatch() blocks every other action for this player until
// resolveCalderaDragonDiscard (below) is called with their choice - see
// PlayerDock.vue's gameState watcher for how the UI picks this up (reuses
// the existing pendingChoice inline hand-highlighting, not a new overlay).
function applyCalderaDragonDiscard(state, playerId, handSizeBeforeAbility) {
  if (!isCalderaDragonPresent(state)) return state;
  const player = getPlayerById(state, playerId);
  if (player.hand.length <= handSizeBeforeAbility) return state; // no net card gained
  return { ...state, meta: { ...state.meta, pendingCalderaDragonDiscard: { playerId } } };
}

// describeAction (actions/index.js) builds this action's own log line
// directly from the resulting state - unlike applyCalderaDragonDiscard's old
// auto-pick version, this IS the main action now (not a side effect tacked
// onto a differently-described one), so no pendingGlobalLogMessage needed.
export function resolveCalderaDragonDiscard(state, { playerId, instanceId }) {
  const pending = state.meta.pendingCalderaDragonDiscard;
  if (!pending || pending.playerId !== playerId) {
    throw new Error('No Caldera Dragon discard is currently owed.');
  }
  const player = getPlayerById(state, playerId);
  if (!player.hand.includes(instanceId)) throw new Error('That card is not in your hand.');
  const discarded = discardFromHand(state, playerId, instanceId);
  return { ...discarded, meta: { ...discarded.meta, pendingCalderaDragonDiscard: null } };
}

function markUsed(state, playerId, key) {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId
      ? { ...p, turnFlags: { ...p.turnFlags, usedAbilityInstanceIds: [...p.turnFlags.usedAbilityInstanceIds, key] } }
      : p)),
  };
}

// Distinct from markUsed above - see turnFlags.js's triggeredAbilityInstanceIds
// comment. Called unconditionally (repeatable or not) right alongside it.
function markTriggered(state, playerId, key) {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId
      ? { ...p, turnFlags: { ...p.turnFlags, triggeredAbilityInstanceIds: [...p.turnFlags.triggeredAbilityInstanceIds, key] } }
      : p)),
  };
}

// End-game stats table: which of the 3 hand-card ability timings actually
// got used, counted separately (not lumped into one "abilities used" total)
// since they read as genuinely different play styles.
const ABILITY_STAT_KEYS = { village: 'villageAbilitiesUsed', dungeon: 'dungeonAbilitiesUsed', spoils: 'spoilsAbilitiesUsed' };
function countAbilityUse(state, playerId, timing) {
  const key = ABILITY_STAT_KEYS[timing];
  if (!key) return state;
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? { ...p, stats: { ...p.stats, [key]: p.stats[key] + 1 } } : p)),
  };
}

// Shared by useVillageAbility/useDungeonAbility/useSpoilsAbility: looks up
// the handler, enforces the once-per-card-per-turn cap (unless the ability
// is marked Repeat), invokes it, and marks it used. allowFromDiscard exists
// only for the just-defeated monster's own Spoils ability (see
// useSpoilsAbility below) - a defeated monster lands straight in discard,
// never in hand, so the normal hand-membership check would make that
// ability permanently unreachable.
function useHandCardAbility(state, {
  playerId, instanceId, timing, abilityIndex = 0, choices, allowFromDiscard = false,
}) {
  const player = getPlayerById(state, playerId);
  const inHand = player.hand.includes(instanceId);
  if (!inHand && !(allowFromDiscard && player.discard.includes(instanceId))) {
    throw new Error('That card is not in your hand.');
  }
  const def = getInstanceDefinition(state, instanceId);
  const entry = (def.abilityText[timing] || [])[abilityIndex];
  if (!entry) throw new Error(`${def.title} has no ${timing} ability at index ${abilityIndex}.`);
  const handler = getHandler(def.id, timing, abilityIndex);
  if (!handler) throw new Error(`${def.title}'s ${timing} ability ("${entry.text}") isn't implemented yet.`);

  const key = usageKey(instanceId, timing, abilityIndex);
  if (!entry.repeatable && player.turnFlags.usedAbilityInstanceIds.includes(key)) {
    throw new Error(`${def.title}'s ability has already been used this turn.`);
  }
  // Numenera, Clever Nano/Enhanced Clever Nano: "This hero can only use one
  // ability." Broader than the standard per-LINE cap just above (which
  // already prevents re-using this SAME line) - blocks the card's OTHER
  // ability line too, once either has been used this turn.
  if (SINGLE_ABILITY_PER_CARD_GROUPS.has(def.group)
    && player.turnFlags.usedAbilityInstanceIds.some((usedKey) => usedKey.startsWith(`${instanceId}:`) && usedKey !== key)) {
    throw new Error(`${def.title} can only use one ability per turn.`);
  }
  if (player.turnFlags.allAbilitiesLocked) {
    throw new Error('Abilities are locked for the rest of this turn.');
  }
  if (timing === 'dungeon' && player.turnFlags.dungeonAbilitiesLocked) {
    throw new Error('Dungeon abilities are locked for the rest of this turn.');
  }
  // Matt reported this directly: "the thunder mage ability should not be
  // allowed to be played after you fought a monster in the dungeon row
  // because the order of things is important... it goes dungeon abilities
  // and then battle effects, and then aftermath effects - after that
  // there's no opportunity to do another dungeon ability." Rulebook turn
  // structure ("The Dungeon"): Dungeon abilities happen before choosing a
  // monster, never after - this is the engine-level guard matching
  // PlayerDock.vue's isAbilityCurrentlyUsable (which hides the button), so
  // the rule holds even if a client dispatches this directly.
  if (timing === 'dungeon' && player.turnFlags.foughtMonsterThisTurn) {
    throw new Error('Dungeon abilities can only be used before fighting a monster this turn.');
  }
  // Rulebook (p.16-17): "you cannot use its Dungeon abilities unless it is
  // equipped to a hero" - see WEAPON_DUNGEON_ABILITY_EQUIP_EXEMPT_IDS above
  // for the one printed exception.
  if (timing === 'dungeon' && def.traits.includes('Weapon')
    && !WEAPON_DUNGEON_ABILITY_EQUIP_EXEMPT_IDS.has(def.id)
    && !isWeaponEquipped(player, instanceId)) {
    throw new Error(`${def.title} must be equipped to a hero to use its Dungeon ability.`);
  }
  // Root of Corruption, War Incarnate's Trophy ("Ignore 1 hero with the
  // highest Strength" - effectHelpers.js's markInstanceIgnored): "no...
  // Dungeon abilities, or any other benefit" - Village/Dungeon abilities
  // are blocked, but the rulebook explicitly carves out an exception for
  // Spoils ("since those occur after the monster has been defeated").
  if (timing !== 'spoils' && player.turnFlags.ignoredInstanceIds?.includes(instanceId)) {
    throw new Error(`${def.title} is ignored for the rest of this turn and cannot use ${timing} abilities.`);
  }

  const handSizeBeforeAbility = player.hand.length;
  let nextState = handler(state, { playerId, instanceId, choices: choices || {} });
  // Caverns of Bane, Skinshifter Terror (L3): "If the card was a disease,
  // you may use this ability a second time this turn." A CONDITIONAL
  // repeat, decided at resolve-time by the handler itself (unlike `entry.
  // repeatable`, which is an unconditional per-line flag known ahead of
  // time) - the handler signals it via meta.bypassAbilityUsageCap rather
  // than knowing how to skip markUsed itself, same "handler signals,
  // dispatch acts" shape as meta.endTurnOverride below. Cleared immediately
  // after being consumed so it never leaks into a later, unrelated ability
  // use this same turn.
  const bypassUsageCap = nextState.meta.bypassAbilityUsageCap;
  if (bypassUsageCap) nextState = { ...nextState, meta: { ...nextState.meta, bypassAbilityUsageCap: false } };
  // Into the Abyss, Xenomorph: "This card becomes a duplicate of..." - the
  // ONLY handler in the engine that changes instanceId's own definitionId
  // as a side effect of running. Matt reported this directly: "the
  // Xeno[morph] duplicated a Regian, and it was unable to use its newly
  // acquired dungeon ability because the Regian it duplicated already had
  // used its dungeon ability, but the newly created card should still be
  // able to use its dungeon ability so we don't want to carry over the
  // status of the card that gets duplicated." `key` is
  // instanceId:timing:abilityIndex - Xenomorph's own "become a duplicate"
  // line happens to share timing+index with plenty of single-line dungeon
  // heroes' (Regian included) own ability, so marking that key used would
  // silently block the BRAND NEW identity's unrelated ability at that same
  // index, the instant it's acquired. If this handler's own execution just
  // changed what instanceId even IS, none of its usage bookkeeping still
  // describes the card sitting there now - skip both markUsed and
  // markTriggered entirely rather than recording history for an identity
  // that no longer exists.
  const identityChanged = getInstanceDefinition(nextState, instanceId).id !== def.id;
  if (!identityChanged) {
    if (!entry.repeatable && !bypassUsageCap) nextState = markUsed(nextState, playerId, key);
    nextState = markTriggered(nextState, playerId, key);
  }
  nextState = countAbilityUse(nextState, playerId, timing);
  nextState = applyCalderaDragonDiscard(nextState, playerId, handSizeBeforeAbility);

  // A card's own text can override the default step order and end the turn
  // early (rulebook's First Rule: "if a card's text contradicts a rule, the
  // card's text takes priority" - e.g. King Caelan's Writ, Thundermage
  // Bolter). Handlers signal this by setting meta.endTurnOverride rather
  // than knowing how to end a turn themselves. If the handler also just
  // queued a mandatory pending hand choice (e.g. Thundermage Bolter's hall
  // refill revealing a Raid monster), the turn can't end yet - endTurn would
  // discard the whole hand out from under an unresolved choice. Leave
  // endTurnOverride set in that case; handChoiceActions.js's
  // dropDeadEntries fires the deferred endTurn once the queue actually
  // drains instead.
  if (nextState.meta.endTurnOverride && !nextState.meta.pendingHandChoices?.length) {
    nextState = endTurn({ ...nextState, meta: { ...nextState.meta, endTurnOverride: false } });
  }
  return nextState;
}

export function useVillageAbility(state, payload) {
  return useHandCardAbility(state, { ...payload, timing: 'village' });
}

export function useDungeonAbility(state, payload) {
  return useHandCardAbility(state, { ...payload, timing: 'dungeon' });
}

// Trophy effects are mandatory whenever a card with one is present in hand,
// for the two turn actions that actually reveal your hand - Visit the
// Village and Enter the Dungeon (rulebook, "Trophy effects"; Caverns of Bane
// errata explicitly corrects the base rules on this point: "When a player
// takes the Prepare or Rest action, Trophy effects do not activate. The
// active player is not required to reveal his or her hand." - see
// actions/index.js's PREPARE/REST cases, which deliberately do NOT call
// this). Resolved once, automatically, the moment a turn's action is chosen
// (i.e. once the hand for this turn is fixed) - this is a simplification of
// the rulebook's "any order, intermixed with other abilities" latitude,
// which would need a full choice-prompt UI to offer meaningfully.
// Shared by both the always-timed 'trophy' bucket and the Dungeon-only
// 'trophyDungeon' bucket below - identical resolution mechanics, just a
// different ability-text key and usage-key namespace so the two can never
// collide even on the same card.
function resolveTrophyBucket(state, playerId, timing) {
  const player = getPlayerById(state, playerId);
  let nextState = state;
  for (const instanceId of player.hand) {
    const def = getInstanceDefinition(nextState, instanceId);
    (def.abilityText[timing] || []).forEach((entry, abilityIndex) => {
      const key = usageKey(instanceId, timing, abilityIndex);
      const current = getPlayerById(nextState, playerId);
      if (current.turnFlags.usedAbilityInstanceIds.includes(key)) return;
      const handler = getHandler(def.id, timing, abilityIndex);
      if (!handler) return; // not yet implemented - silently skipped, not a hard error
      // Trophy effects are mandatory and fully automatic - the player never
      // chose to trigger this, so a handler bug or an unmet precondition it
      // didn't anticipate must never crash the turn. Any error here is a
      // real bug worth knowing about (hence the warning), but the effect is
      // skipped rather than blocking every other action for the rest of the
      // game.
      try {
        nextState = handler(nextState, { playerId, instanceId, choices: {} });
        nextState = markUsed(nextState, playerId, key);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[trophy-effect-error] ${def.id}:${timing}:${abilityIndex} threw, skipping: ${err.message}`);
        nextState = markUsed(nextState, playerId, key);
      }
    });
  }
  return nextState;
}

// `phase` is optional (defaults to resolving only the always-timed 'trophy'
// bucket, matching every caller before Horror Incarnate's "Trophy Dungeon"
// existed) - pass PHASES.DUNGEON from the ENTER_DUNGEON call site to also
// resolve 'trophyDungeon' entries. Root of Corruption, Horror Incarnate;
// rulebook FAQ, "What's a 'Trophy Dungeon'?": "This special Trophy effect
// only applies in the Dungeon. Its effect does not happen in the village,
// only when a player has chosen the enter the dungeon action." - so
// VISIT_VILLAGE must never resolve this bucket.
export function resolveTrophyEffects(state, playerId, phase) {
  let nextState = resolveTrophyBucket(state, playerId, 'trophy');
  if (phase === PHASES.DUNGEON) {
    nextState = resolveTrophyBucket(nextState, playerId, 'trophyDungeon');
  }
  return nextState;
}

// Root of Corruption, Circle of Protection: "On your turn, place this card
// in front of you." Same two call sites and same "not required to reveal
// your hand on Prepare/Rest" scoping as resolveTrophyEffects just above -
// this only fires on the two actions that actually reveal your hand, moving
// every copy currently in hand into the new "in front of you" zone
// (effectHelpers.js's placeInFrontOfPlayer). Unconditional (no "may" in the
// printed text), and, unlike Trophy, has no once-per-turn usage cap to
// track - each copy can only be placed once anyway, since placing it moves
// it straight out of hand.
export function resolveCircleOfProtectionPlacement(state, playerId) {
  const player = getPlayerById(state, playerId);
  const inHandCopies = player.hand.filter((id) => getInstanceDefinition(state, id).id === 'village-circle-of-protection');
  return inHandCopies.reduce((nextState, instanceId) => placeInFrontOfPlayer(nextState, playerId, instanceId), state);
}

// Numenera, Unstable Crystals: "specialEffects: If 3 or more copies of this
// card are present, destroy all copies of this card and 1 random card. End
// your turn." Same two call sites and same hand-reveal-only scoping as
// resolveTrophyEffects/resolveCircleOfProtectionPlacement above - "present"
// reads the same as those two ("present in your hand"), so it's checked
// once, automatically, right alongside them. The "1 random card" needs real
// seeded-random selection (effectHelpers.js's pickRandomInstanceId, first
// built for Gnoll's Thieving Cur/Scavenging Cur - see that file's own
// comment anticipating exactly this reuse) drawn from whatever's left in
// hand AFTER the guaranteed crystal-destruction, not the original full
// hand (the crystals are already covered by "destroy all copies of this
// card" - "1 random card" reads as an ADDITIONAL, different card). Calls
// endTurn() directly here (rather than setting meta.endTurnOverride the
// way a hand-card-ability handler would, since useHandCardAbility is the
// only call site that ever checks that flag, and this doesn't go through
// it) - safe because nothing here queues a pending choice that would need
// to drain first.
export function resolveUnstableCrystalsCascade(state, playerId) {
  const player = getPlayerById(state, playerId);
  const crystalIds = player.hand.filter((id) => getInstanceDefinition(state, id).id === 'village-unstable-crystals');
  if (crystalIds.length < 3) return state;
  let nextState = crystalIds.reduce((s, id) => destroyFromHand(s, playerId, id), state);
  const remainingHand = getPlayerById(nextState, playerId).hand;
  const { state: afterPick, instanceId: randomId } = pickRandomInstanceId(nextState, remainingHand);
  nextState = randomId ? destroyFromHand(afterPick, playerId, randomId) : afterPick;
  return endTurn(nextState);
}

// Spoils abilities are only usable in the brief window right after winning a
// fight, from cards present in hand (rulebook, "Spoils:"). meta.spoilsWindowOpen
// is set by dungeonActions.chooseMonster and cleared by ending the turn or
// starting another fight.
export function useSpoilsAbility(state, payload) {
  if (!state.meta.spoilsWindowOpen) throw new Error('Spoils abilities are only usable right after winning a fight.');
  const player = getPlayerById(state, payload.playerId);
  // Matt: "spoils abilities as I understand them only trigger on the turn
  // in which you defeat that enemy... I'm here on a future turn having
  // drawn the Scabbark and it's allowing me to use the ability." Rulebook,
  // "Spoils Abilities": "The only exception is monster cards in your hand.
  // You cannot use those Spoils abilities again" - illustrated by the
  // rulebook's own Scabbark example (a previously-defeated Scabbark
  // redrawn into hand on a later turn: "he doesn't get to use it again.
  // That fight is long over."). A monster's Spoils ability is legal only
  // in the fight where THAT instance was just defeated - never from a
  // trophy copy sitting in hand from some earlier turn.
  const def = getInstanceDefinition(state, payload.instanceId);
  const isJustDefeatedMonster = payload.instanceId === state.meta.lastCombatResult?.monsterInstanceId;
  if (def.type === 'monster' && !isJustDefeatedMonster) {
    throw new Error(`${def.title}'s Spoils ability can only be used the fight it was defeated - that fight is long over.`);
  }
  if (player.turnFlags.spoilsCancelled) {
    // Matt reported this directly: "I got a pop-up that said spoils
    // abilities are cancelled this combat but I don't see where or why that
    // would've been the case." The rule itself was correct (only Marid's
    // Battle effect sets this - "Gain 1 curse if you have no thief. Cancel
    // all Spoils abilities." - the second sentence is unconditional, so it
    // still cancels spoils even if you had a thief and avoided the curse,
    // which reads as a non sequitur if the monster's own name isn't right
    // there in the message). Name the monster so the "why" is obvious
    // without having to cross-reference the game log or the printed card.
    const monsterTitle = state.meta.lastCombatResult?.monsterDef?.title;
    throw new Error(monsterTitle
      ? `${monsterTitle}'s Battle effect cancelled all Spoils abilities this fight.`
      : 'Spoils abilities are cancelled this combat.');
  }
  // A just-defeated monster lands straight in the winner's discard pile
  // (see resolveCombat.js), never in hand - allowFromDiscard lets its own
  // Spoils ability still be used in that one legitimate window.
  return useHandCardAbility(state, { ...payload, timing: 'spoils', allowFromDiscard: isJustDefeatedMonster });
}

// village-smuggler's React ("Destroy this card after a player loses a
// combat. Buy 1 card.") - same window-gated shape as useSpoilsAbility
// above (meta.smugglerReactWindowOpen, opened by combat/resolveCombat.js on
// a loss with an eligible Smuggler in hand, closed by endTurn or the next
// fight), but dispatched as its own action rather than folded into
// useHandCardAbility's generic timing dispatch - "Buy 1 card" needs a real
// `source` choice (which stack to buy from), the same way Bounty
// Hunter/Rapparee's own Spoils-buy abilities do, which only a genuine
// follow-up action (not a Battle/Aftermath-style queued pick) can offer.
// "Destroy," not discard (village-smuggler's own printed text) - the
// Smuggler leaves the game, it doesn't cycle back through its owner's deck.
export function useSmugglerReact(state, { playerId, instanceId, source }) {
  if (!state.meta.smugglerReactWindowOpen) throw new Error('Smuggler\'s React is only usable right after losing a fight.');
  const player = getPlayerById(state, playerId);
  if (!player.hand.includes(instanceId)) throw new Error('That card is not in your hand.');
  if (getInstanceDefinition(state, instanceId).id !== 'village-smuggler') throw new Error('That card is not a Smuggler.');
  const destroyed = destroyFromHand(state, playerId, instanceId);
  return performBuy(destroyed, playerId, source, { requireGoldCheck: false });
}
